import { createContext, type FC, type ReactNode, useContext } from "react";

import type { IEventFilters } from "~/types/eventFilters";

/**
 * Provider-agnostic contract the shared filter components work against.
 * Any filter provider (dashboard, event markets, …) can expose its state
 * through this scope, making every component under ~/components/filters
 * reusable in that context without per-feature wrappers.
 */
export interface IFiltersScope {
  filters: Partial<IEventFilters>;
  updateFilters: (patch: Partial<IEventFilters>) => void;
  clearAllFilters: () => void;
  isFiltersApplied: boolean;
  registerOnClearAllFilters: (cb: () => void) => () => void;
}

const FiltersScopeContext = createContext<IFiltersScope | null>(null);

interface IFiltersScopeProviderProps {
  value: IFiltersScope;
  children: ReactNode;
}

export const FiltersScopeProvider: FC<IFiltersScopeProviderProps> = ({
  value,
  children,
}) => (
  <FiltersScopeContext.Provider value={value}>
    {children}
  </FiltersScopeContext.Provider>
);

export const useFiltersScope = (): IFiltersScope => {
  const context = useContext(FiltersScopeContext);

  if (!context) {
    throw new Error(
      "useFiltersScope must be used within a FiltersScopeProvider"
    );
  }

  return context;
};
